import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import TaxPriceForm from "../../../components/forms/TaxPriceForm";
import { partialUpdateFiling } from "../../../api/filings/filingAPI";
import { Handler401 } from "../../../api/utils/responseActions";
import "../../../styles/v2/components/lists.css";

function TaxPrice({ filingData, onNext, onPrev }) {
  const [searchParams] = useSearchParams();

  // Check if returning from successful Stripe payment
  const redirectStatus = searchParams.get("redirect_status");
  const comingFromStripePayment = redirectStatus === "succeeded";

  // Skip audit protection screen if payment already accepted OR coming from Stripe
  const paymentAlreadyAccepted = filingData?.payment_accepted || false;
  const skipAuditProtection = paymentAlreadyAccepted || comingFromStripePayment;

  const [showCheckout, setShowCheckout] = useState(skipAuditProtection);
  const [auditProtectionSelected, setAuditProtectionSelected] = useState(
    filingData?.audit_protection || false
  );
  const [updatedFilingData, setUpdatedFilingData] = useState(filingData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { filingUuid } = useParams();
  const navigate = useNavigate();

  const handleGoToCheckout = async () => {
    setLoading(true);
    setError(null);

    try {
      const requestData = {
        audit_protection: auditProtectionSelected,
        calculate_price: true,
      };

      const response = await partialUpdateFiling(
        "tax/filing",
        requestData,
        filingUuid
      );

      if (response.status === 401) {
        Handler401(navigate);
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to update filing with audit protection");
      }

      const updatedFiling = await response.json();
      setUpdatedFilingData(updatedFiling);
      setLoading(false);
      setShowCheckout(true);
    } catch (err) {
      console.error("Error updating audit protection:", err);
      setError("Failed to update audit protection. Please try again.");
      setLoading(false);
    }
  };

  // Show Audit Protection screen first
  if (!showCheckout) {
    return (
      <div className="audit-protection-container">
        <div
          className="audit-protection-header"
          style={{ display: "flex", alignItems: "center", gap: "12px" }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/shield_protection.svg`}
            alt="Audit Protection"
            className="audit-protection-icon"
            style={{ width: "40px", height: "40px" }}
          />
          <h2 className="audit-protection-title" style={{ margin: "0" }}>
            Optional Audit Protection
          </h2>
        </div>

        <div className="audit-protection-content">
          <p className="audit-protection-description">
            Extra peace of mind, just in case. If the IRS ever has questions
            about this return, you won’t have to handle it alone. A licensed tax
            professional will step in, help manage communications, and guide you
            through the process.
            <br></br>
            <br></br>
            What this includes:
          </p>

          <ul className="audit-protection-benefits">
            <li>
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/verified_blue_badget.svg`}
                alt=""
                className="benefit-icon"
              />
              Help responding to IRS notices related to this return
            </li>
            <li>
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/verified_blue_badget.svg`}
                alt=""
                className="benefit-icon"
              />
              Representation by a licensed tax professional
            </li>
            <li>
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/verified_blue_badget.svg`}
                alt=""
                className="benefit-icon"
              />
              Coverage specific to this filing, if it’s reviewed
            </li>
          </ul>

          <div className="audit-protection-checkbox-container">
            <label className="audit-protection-checkbox-label">
              <div className="audit-protection-checkbox-wrapper">
                <input
                  type="checkbox"
                  checked={auditProtectionSelected}
                  onChange={(e) => setAuditProtectionSelected(e.target.checked)}
                  className="audit-protection-checkbox"
                />
              </div>
              <span>Add Audit Protection for $45</span>
            </label>
            <br></br>
            <p>
              A licensed expert handles IRS audit communications for this
              return.
            </p>
          </div>
        </div>

        {error && (
          <div className="tax-price-demo-error" style={{ marginTop: "16px" }}>
            {error}
          </div>
        )}

        <button
          className="cta-button primary audit-protection-cta"
          onClick={handleGoToCheckout}
          disabled={loading}
        >
          {loading ? "Updating..." : "Proceed to Checkout"}
        </button>
      </div>
    );
  }

  // Show TaxPriceForm (checkout) after audit protection selection
  return (
    <TaxPriceForm filing={updatedFilingData} onNext={onNext} onPrev={onPrev} />
  );
}

export default TaxPrice;
